'use client'
import React, { useEffect, useRef, useState } from 'react'
import { CircleChevronDown, CircleChevronUp } from 'lucide-react'

type Heading = {
  id: string
  title: string
}

// Ensure H2s have IDs so links work
function ensureHeadingIds(levels: string[] = ['h2']) {
  levels.forEach((level) => {
    document.querySelectorAll<HTMLElement>(level).forEach((el, idx) => {
      if (!el.id) el.id = `${level}-heading-${idx}`
    })
  })
}

function useHeadingsData(levels: string[] = ['h2']) {
  const [headings, setHeadings] = useState<Heading[]>([])
  useEffect(() => {
    ensureHeadingIds(levels)
    const headingElements = Array.from(document.querySelectorAll<HTMLElement>(levels.join(',')))
    // Exclude headings inside footers
    const filtered = headingElements.filter((heading) => {
      let parent = heading.parentElement
      while (parent) {
        if (parent.tagName.toLowerCase() === 'footer') return false
        parent = parent.parentElement
      }
      return true
    })
    setHeadings(
      filtered.map((h) => ({
        id: h.id,
        title: h.textContent || '',
      })),
    )
  }, [levels])
  return { headings }
}

function useIntersectionObserver(
  setActiveId: React.Dispatch<React.SetStateAction<string | undefined>>,
  levels: string[] = ['h2'],
  activeId?: string,
) {
  const headingElementsRef = useRef<Record<string, IntersectionObserverEntry>>({})
  useEffect(() => {
    const callback = (entries: IntersectionObserverEntry[]) => {
      headingElementsRef.current = entries.reduce((map, entry) => {
        map[entry.target.id] = entry
        return map
      }, headingElementsRef.current)

      const visible: IntersectionObserverEntry[] = []
      Object.values(headingElementsRef.current).forEach((entry) => {
        if (entry.isIntersecting) visible.push(entry)
      })

      const headingElements = Array.from(document.querySelectorAll<HTMLElement>(levels.join(',')))
      const getIndexFromId = (id: string) => headingElements.findIndex((h) => h.id === id)

      if (visible.length === 1 && visible[0]?.target?.id) {
        setActiveId(visible[0].target.id)
      } else if (visible.length > 1) {
        const sorted = visible.sort(
          (a, b) => getIndexFromId(a.target.id) - getIndexFromId(b.target.id),
        )
        if (sorted[0]?.target?.id) setActiveId(sorted[0].target.id)
      } else if (activeId) {
        const activeElement = headingElements.find((el) => el.id === activeId)
        const activeIndex = headingElements.findIndex((el) => el.id === activeId)
        const y = activeElement?.getBoundingClientRect().y
        if (y !== undefined && y > 150 && activeIndex > 0)
          setActiveId(headingElements[activeIndex - 1]?.id)
      }
    }

    const observer = new window.IntersectionObserver(callback, { rootMargin: '0px 0px -40% 0px' })
    const headingElements = Array.from(document.querySelectorAll<HTMLElement>(levels.join(',')))
    headingElements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [setActiveId, levels, activeId])
}

// Offset for hero paddings: pt-32 md:pt-36 lg:pt-36 xl:pt-40
function getScrollOffset() {
  if (typeof window === 'undefined') return 0
  if (window.innerWidth >= 1280) return 160 // xl:pt-40 (40*4)
  if (window.innerWidth >= 1024) return 144 // lg:pt-36 (36*4)
  if (window.innerWidth >= 768) return 144 // md:pt-36
  return 128 // pt-32 (32*4)
}

const TableOfContentBlock: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true)
  const [activeId, setActiveId] = useState<string | undefined>()
  const levels = ['h2']
  const { headings } = useHeadingsData(levels)
  useIntersectionObserver(setActiveId, levels, activeId)

  const scrollWithOffset = (el: HTMLElement) => {
    const y = el.getBoundingClientRect().top + window.pageYOffset - getScrollOffset()
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  if (!headings?.length) return null

  return (
    <div className="w-full flex justify-center">
      <div className="toc-block border rounded-xl px-4 py-2 mb-4 max-w-2xl w-full">
        <button
          className="font-bold mb-2 w-full flex items-center justify-center"
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
        >
          On this page
          {collapsed ? (
            <CircleChevronDown className="ml-2 inline-block" size={18} aria-hidden="true" />
          ) : (
            <CircleChevronUp className="ml-2 inline-block" size={18} aria-hidden="true" />
          )}
        </button>
        {!collapsed && (
          <ul className="list-disc pl-6">
            {headings.map((heading) => (
              <li key={heading.id} className={heading.id === activeId ? 'active' : ''}>
                <a
                  href={`#${heading.id}`}
                  className={
                    heading.id === activeId
                      ? 'text-[#971a1a] font-bold underline'
                      : 'hover:underline'
                  }
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const el = document.querySelector(`#${heading.id}`)
                    if (el) scrollWithOffset(el as HTMLElement)
                  }}
                >
                  {heading.title}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default TableOfContentBlock
